import React from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../components/ui/InputField';
import SelectField from '../../../components/ui/SelectField';
import TextareaField from '../../../components/ui/TextareaField';
import OrgDetailSection from './OrgDetailSection';
import {
    CUSTOMER_ORGANIZATION_TYPES,
    CustomerOrganizationType,
    ORGANIZATION_STATUSES,
    OrganizationStatus,
} from '../../../api/organization/organizationApiTypes';
import { ORGANIZATION_TYPE_LABEL_KEY } from '../organizationTypeLabels';

export interface OrgBasicInfo {
    legalName: string;
    displayName: string;
    /**
     * Empty until the operator chooses. The API requires a valid type
     * (`serializers.ts:organizationPayloadToColumns`), so this is a genuine
     * required field — it used to be pre-set to `club`, which meant a company
     * created without touching the select was silently filed as a club and
     * nothing could tell that apart from a deliberate choice.
     */
    type: CustomerOrganizationType | '';
    /**
     * #2102 item 1. A LABEL, not a gate — nothing refuses a booking or hides
     * the org from a picker because of it. Defaults to `active`, which is also
     * the column default, so create and edit agree without a special case.
     */
    status: OrganizationStatus;
    /**
     * The contact person, entered as an e-mail (#2163).
     *
     * NOT a column on the organization — it is the member carrying the `owner`
     * role, which the backend derives from this address: an existing account
     * is designated straight away, an unknown one gets an owner-invite and
     * becomes the contact when they first sign in. A free-text name/phone pair
     * was rejected in #2102 because it drifts from the member list that holds
     * the fact; this field writes that list rather than shadowing it.
     *
     * Several contacts are allowed since #3037. On create this names the
     * first one; on edit (`contactFieldMode="add"`) it ADDS one and an empty
     * field changes nothing — existing contacts are managed in the member
     * list, where the star marks each of them.
     */
    contactEmail: string;
    notes: string;
}

interface OrgBasicInfoSectionProps {
    value: OrgBasicInfo;
    onChange: (updates: Partial<OrgBasicInfo>) => void;
    /** Mark legal_name invalid (e.g. on a save attempt with it empty). */
    legalNameError?: boolean;
    /** Mark type invalid (e.g. on a save attempt with nothing chosen). */
    typeError?: boolean;
    /** Render read-only (view mode): inputs disabled, no editing. */
    disabled?: boolean;
    /**
     * `initial` (create): names the first contact person. `add` (edit): adds
     * another one and is hidden in view mode — with several contacts there
     * is no single value to show; the member list marks each of them (#3037).
     */
    contactFieldMode?: 'initial' | 'add';
}

const OrgBasicInfoSection: React.FC<OrgBasicInfoSectionProps> = ({
    value,
    onChange,
    legalNameError = false,
    typeError = false,
    disabled = false,
    contactFieldMode = 'initial',
}) => {
    const { t } = useTranslation();

    const typeLabels = React.useMemo(() => {
        const labels: Record<string, string> = {};
        CUSTOMER_ORGANIZATION_TYPES.forEach((type) => {
            labels[type] = t(ORGANIZATION_TYPE_LABEL_KEY[type]);
        });
        return labels;
    }, [t]);

    const statusLabels = React.useMemo(() => {
        const labels: Record<string, string> = {};
        ORGANIZATION_STATUSES.forEach((status) => {
            labels[status] = t(`organization.status.${status}`);
        });
        return labels;
    }, [t]);

    return (
        <OrgDetailSection title={t('organization.sections.basic_info')}>
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-1'}>
                    <InputField
                        labelAbove={true}
                        label={`${t('organization.fields.legal_name')} *`}
                        help={t('organization.help.legal_name')}
                        placeholder={t('organization.fields.legal_name_placeholder')}
                        value={value.legalName}
                        onChange={(val) => onChange({ legalName: val })}
                        error={legalNameError}
                        disabled={disabled}
                    />
                    {legalNameError && (
                        <p className={'text-xs text-red-600'}>
                            {t('organization.validation.legal_name_required')}
                        </p>
                    )}
                </div>
                <InputField
                    labelAbove={true}
                    label={t('organization.fields.display_name')}
                    help={t('organization.help.display_name')}
                    placeholder={t('organization.fields.display_name_placeholder')}
                    value={value.displayName}
                    onChange={(val) => onChange({ displayName: val })}
                    disabled={disabled}
                />
                {/* Same label key as the tenant account section
                    (`AccountPage.tsx`) so the wording of "type of organization"
                    can never drift between the two surfaces — the option labels
                    already come from `organization_type.*`. */}
                <div className={'flex flex-col gap-1'}>
                    <SelectField
                        labelAbove={true}
                        label={`${t('organization_type.label')} *`}
                        help={t('organization.help.type')}
                        // No preselection: the operator chooses. A guessed
                        // default here is silently accepted and unrecoverable —
                        // "filed as a club because nobody looked" and "really a
                        // club" become the same record.
                        placeholder={t('common.please_select')}
                        value={value.type}
                        onChange={(val) => onChange({ type: val as CustomerOrganizationType })}
                        options={[...CUSTOMER_ORGANIZATION_TYPES]}
                        optionLabels={typeLabels}
                        error={typeError}
                        disabled={disabled}
                    />
                    {typeError && (
                        <p className={'text-xs text-red-600'}>
                            {t('organization.validation.type_required')}
                        </p>
                    )}
                </div>
                {/* #2163. Next to the identity fields because "who do I call
                    here" is one of them — and this is the field an operator
                    looks for while creating a customer, which is exactly where
                    it was missing. */}
                {!(contactFieldMode === 'add' && disabled) && (
                    <InputField
                        labelAbove={true}
                        label={t(
                            contactFieldMode === 'add'
                                ? 'organization.fields.contact_email_add'
                                : 'organization.fields.contact_email',
                        )}
                        help={t(
                            contactFieldMode === 'add'
                                ? 'organization.help.contact_email_add'
                                : 'organization.help.contact_email',
                        )}
                        placeholder={t('organization.fields.contact_email_placeholder')}
                        value={value.contactEmail}
                        onChange={(val) => onChange({ contactEmail: val })}
                        disabled={disabled}
                    />
                )}
                {/* #2102 item 1. Deliberately next to type rather than in a
                    section of its own: it is one more attribute of what this
                    organization IS, and a lone status control invites reading
                    it as an action. */}
                <SelectField
                    labelAbove={true}
                    label={t('organization.fields.status')}
                    help={t('organization.help.status')}
                    value={value.status}
                    onChange={(val) => onChange({ status: val as OrganizationStatus })}
                    options={[...ORGANIZATION_STATUSES]}
                    optionLabels={statusLabels}
                    disabled={disabled}
                />
                <TextareaField
                    labelAbove={true}
                    label={t('organization.fields.notes')}
                    placeholder={t('organization.fields.notes_placeholder')}
                    value={value.notes}
                    onChange={(val) => onChange({ notes: val })}
                    rows={3}
                    disabled={disabled}
                />
            </div>
        </OrgDetailSection>
    );
};

export default OrgBasicInfoSection;
